import { Box, Divider, Flex, Icon, Table } from '@customerio/pluma-components/react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { copyToClipboard } from '@/hooks/user-actions/use-copy-to-clipboard';
import { download } from '@/utils/download';
import { isEmptyString } from '@/utils/string';
import { type ReactNode, useCallback, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { stringify } from '@vanillaes/csv';
import slugify from 'slugify';
import * as styles from './markdown-table.css';
import { MessageAction, MessageActionsContainer } from '../message-container';

/**
 * Extracts text content from a table cell for CSV export.
 *
 * If a cell contains only a link, the href is used instead of the display text
 * so exported CSV includes actual URLs rather than labels like "View" — EXCEPT
 * for autolinked emails/phone numbers (GFM turns a bare address into a
 * `mailto:`/`tel:` link) and links whose href was stripped by sanitization.
 * There the href is empty or a `mailto:`-prefixed duplicate of the address,
 * which would blank or mangle the column, so the visible text is kept.
 * (Regression: the agent-chat table export dropped the entire email column
 * because emails autolink to `mailto:` and the stripped href exported empty.)
 */
function extractCellText(cell: Element): string {
  const text = cell.textContent?.trim() ?? '';
  const link = cell.querySelector('a');
  if (link != null && link.textContent?.trim() === text) {
    const rawHref = link.getAttribute('href')?.trim() ?? '';
    if (!isEmptyString(rawHref) && !rawHref.startsWith('mailto:') && !rawHref.startsWith('tel:')) {
      return link.href;
    }
  }
  return text;
}

interface MarkdownTableProps {
  children: ReactNode;
  caption?: string;
}

/**
 * Wrapper component for tables in AI Assistant markdown that adds:
 * - Export to CSV functionality
 * - Hover highlighting for improved readability
 */
export function MarkdownTable({ children, caption = 'Data table' }: MarkdownTableProps) {
  const tableRef = useRef<HTMLDivElement>(null);
  const snackbars = useSnackbars();
  const { track } = useAnalytics();

  const getTableData = useCallback(() => {
    if (tableRef.current == null) {
      throw new Error('Table reference not available');
    }

    const table = tableRef.current.querySelector('table');
    if (table == null) {
      throw new Error('Table not found');
    }

    const rows: string[][] = [];

    // Extract headers
    const headers = Array.from(table.querySelectorAll('thead th')).map((th) => {
      return th.textContent?.trim() ?? '';
    });

    if (headers.length > 0) {
      rows.push(headers);
    }

    // Extract data rows
    const dataRows = table.querySelectorAll('tbody tr');
    for (const tr of dataRows) {
      const cells = Array.from(tr.querySelectorAll('td, th')).map((cell) => {
        return extractCellText(cell);
      });
      if (cells.length > 0) {
        rows.push(cells);
      }
    }

    return { headers, rows };
  }, []);

  const generateCSV = useCallback(() => {
    const { rows } = getTableData();
    return stringify(rows);
  }, [getTableData]);

  const exportMutation = useMutation({
    mutationFn: async () => {
      const { headers, rows } = getTableData();
      const csv = stringify(rows);
      const name = headers.filter((h) => !isEmptyString(h)).join(' ');
      const slug = slugify(name, { lower: true, strict: true });
      const filename = `${!isEmptyString(slug) ? slug : 'table'}.csv`;
      await download(csv, filename, { autoBom: true });
    },
    onSuccess: () => {
      track('aiAssistant', 'tableExported', {});
      snackbars.create({
        message: 'Table exported.',
      });
    },
    onError: () => {
      snackbars.create({
        message: "Table couldn't be exported. Try again.",
        variant: 'error',
      });
    },
  });

  const copyMutation = useMutation({
    mutationFn: async () => {
      const csv = generateCSV();
      const success = await copyToClipboard(csv);
      if (!success) {
        throw new Error('Failed to copy');
      }
    },
    onSuccess: () => {
      track('aiAssistant', 'tableCopied', {});
      snackbars.create({
        message: 'Table copied to clipboard.',
      });
    },
    onError: () => {
      snackbars.create({
        message: "Table couldn't be copied. Try again.",
        variant: 'error',
      });
    },
  });

  return (
    <Flex direction="column" className={styles.tableContainer}>
      <Box overflowX="auto" w="full" ref={tableRef}>
        <Table caption={caption} shouldHighlightOnHover={true} w="full">
          {children}
        </Table>
      </Box>
      <Divider />
      <Box pt="100">
        <MessageActionsContainer isVisible={true} alignBlock="center" alignInline="start">
          <MessageAction
            tooltip="Copy table as CSV"
            onClick={() => copyMutation.mutate()}
            aria-label="Copy table as CSV"
          >
            <Icon name="copy-to-clipboard" size="xs" />
          </MessageAction>
          <MessageAction
            tooltip="Download table as CSV"
            onClick={() => exportMutation.mutate()}
            aria-label="Download table as CSV"
          >
            {exportMutation.isPending ? <Icon name="loading" size="xs" /> : <Icon name="download" size="xs" />}
          </MessageAction>
        </MessageActionsContainer>
      </Box>
    </Flex>
  );
}
